import { newestFirst } from './profileLinks';

/**
 * One timeline, three homes.
 *
 * Kyu, 9 September, on HubSpot's deal page: "all activities are there, most
 * recent to oldest, whether that's an incoming email, an email sent, a note,
 * a meeting that was booked, a task that was placed."
 *
 * A TimelineItem is the one row vocabulary. Every source (deal changes, the
 * contact's emails and notes, the deal's tasks, its orders) is turned into
 * items by a small builder below, then merged and sorted by the one rule.
 * The component (Timeline.tsx) draws items and knows nothing about tables,
 * so the lead and company panels can feed it their own sources later.
 *
 * Nothing here writes. Every source already exists and is already written.
 */

export type TimelineKind =
  | 'email_in'
  | 'email_out'
  | 'touch' // call, LinkedIn, meeting
  | 'note'
  | 'task'
  | 'change'
  | 'order';

export interface TimelineItem {
  id: string;
  /** ISO timestamp. The sort key, and what the row shows. */
  at: string;
  kind: TimelineKind;
  title: string;
  detail?: string;
  /** Who did it, in words the reader recognises. */
  who?: string;
  /** Where the row goes when clicked, if anywhere. */
  href?: string;
  /**
   * The date already in words. The lead timeline formats a day-only row in
   * UTC and an instant in the reader's zone (leadActivity.ts); when a source
   * has that rule, it hands the label over rather than letting the component
   * guess and show the wrong day.
   */
  when?: string;
  badges?: readonly { label: string; tone: 'brand' | 'warning' | 'success' | 'neutral' }[];
  /**
   * Set when an AI wrote this row. The timeline shows the face in place of the
   * kind glyph, so the reader can tell at a glance which entries were machine
   * written without reading a word of them.
   */
  author?: { name: string; avatarUrl: string };
}

export type TimelineFilter =
  'all' | 'emails' | 'touches' | 'notes' | 'tasks' | 'changes' | 'orders';

export const TIMELINE_FILTERS: readonly { key: TimelineFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'emails', label: 'Emails' },
  { key: 'touches', label: 'Calls & meetings' },
  { key: 'notes', label: 'Notes' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'changes', label: 'Changes' },
  { key: 'orders', label: 'Orders' },
];

const KINDS_IN: Record<Exclude<TimelineFilter, 'all'>, readonly TimelineKind[]> = {
  emails: ['email_in', 'email_out'],
  touches: ['touch'],
  notes: ['note'],
  tasks: ['task'],
  changes: ['change'],
  orders: ['order'],
};

export function filterTimeline(
  items: readonly TimelineItem[],
  filter: TimelineFilter,
): TimelineItem[] {
  if (filter === 'all') return [...items];
  const kinds = KINDS_IN[filter];
  return items.filter((item) => kinds.includes(item.kind));
}

/** Merge any number of sources and apply the one ordering rule. */
export function mergeTimeline(...sources: readonly (readonly TimelineItem[])[]): TimelineItem[] {
  return sources.flat().sort(newestFirst((item) => item.at));
}

/* ── Builders: one per source ─────────────────────────────────────────── */

/** What the deal drawer already knows, handed in so ids become names. */
export interface DealChangeContext {
  stageName: (id: string | null) => string;
  sectionName: (id: string | null) => string;
  personName: (id: string | null) => string;
  amount: (value: number | null) => string;
  outcome: (value: string | null) => string;
}

export interface DealActivityRow {
  id: string;
  /** Present when rows from several deals are merged, so each can be named. */
  deal_id?: string;
  event_type: string;
  details: unknown;
  actor_id: string | null;
  created_at: string;
}

function field(details: unknown, key: string): unknown {
  return details !== null && typeof details === 'object' && key in details
    ? (details as Record<string, unknown>)[key]
    : undefined;
}
const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/**
 * deal_activity rows as Changes. Written by a trigger on every deal edit
 * since 20270119000200 and, until this drop, shown nowhere.
 */
export function dealChangeItems(
  rows: readonly DealActivityRow[],
  ctx: DealChangeContext,
  /** On a lead or company, which deal a change belongs to and where it opens. */
  deal?: (dealId: string) => { name: string; href: string } | undefined,
): TimelineItem[] {
  return rows.map((row) => {
    const from = field(row.details, 'from');
    const to = field(row.details, 'to');
    const who = ctx.personName(row.actor_id);
    const named = deal === undefined || row.deal_id === undefined ? undefined : deal(row.deal_id);
    const base = {
      id: `change-${row.id}`,
      at: row.created_at,
      kind: 'change' as const,
      who: named === undefined ? who : `${who} · ${named.name}`,
      ...(named === undefined ? {} : { href: named.href }),
    };
    switch (row.event_type) {
      case 'created':
        return {
          ...base,
          title: 'Deal created',
          detail: ctx.stageName(str(field(row.details, 'stage_id'))),
        };
      case 'renamed':
        return { ...base, title: 'Renamed', detail: `${str(from) ?? ''} → ${str(to) ?? ''}` };
      case 'stage_changed':
        return {
          ...base,
          title: `Stage ${ctx.stageName(str(from))} → ${ctx.stageName(str(to))}`,
        };
      case 'section_changed':
        return {
          ...base,
          title: `Section ${ctx.sectionName(str(from))} → ${ctx.sectionName(str(to))}`,
        };
      case 'owner_changed':
        return {
          ...base,
          title: `Deal lead ${ctx.personName(str(from))} → ${ctx.personName(str(to))}`,
        };
      case 'amount_changed':
        return { ...base, title: `Amount ${ctx.amount(num(from))} → ${ctx.amount(num(to))}` };
      case 'next_action_changed':
        return { ...base, title: `Next action ${str(from) ?? '—'} → ${str(to) ?? '—'}` };
      case 'outcome_changed': {
        const closedOn = str(field(row.details, 'closed_on'));
        const note = str(field(row.details, 'outcome_note'));
        const toLabel = ctx.outcome(str(to));
        return {
          ...base,
          title: `Marked ${toLabel}`,
          detail: [
            `${ctx.outcome(str(from))} → ${toLabel}`,
            closedOn === null ? null : `closed ${closedOn}`,
            note === null || note.trim() === '' ? null : note,
          ]
            .filter((part): part is string => part !== null)
            .join(' · '),
        };
      }
      case 'hubspot_link_changed':
        return {
          ...base,
          title: str(to) === null ? 'HubSpot link removed' : 'HubSpot link added',
          ...(str(to) === null ? {} : { detail: str(to) ?? '' }),
        };
      case 'deleted':
        return { ...base, title: 'Deal deleted' };
      default:
        return { ...base, title: row.event_type.replace(/_/g, ' ') };
    }
  });
}

/**
 * The two AI authors, keyed by the activity source they write under.
 *
 * They are authors, not people: no profile, no login, no actor_id. A row they
 * wrote is recognised by its source and nothing else, which is why the source
 * is the key here and why there are two of them rather than one generic "AI" —
 * research and copy are different jobs and the timeline has to show whose.
 *
 * Neither is ever a touch. That is decided by KIND, not by this map, and both
 * write kind 'note' (20270153000100).
 */
export const AI_AUTHORS: Record<string, { name: string; avatarUrl: string }> = {
  senku_research: { name: 'Senku', avatarUrl: '/avatars/senku.webp' },
  reigen_copy: { name: 'Reigen', avatarUrl: '/avatars/reigen.webp' },
};

/** The author of a row, when an AI wrote it. Undefined means a person did. */
export function aiAuthorOf(source: string): { name: string; avatarUrl: string } | undefined {
  return AI_AUTHORS[source];
}

/**
 * The title for every activity kind that is not an email or a note.
 *
 * This used to be a chain of ternaries ending in 'Meeting', so any kind it did
 * not name was announced as a meeting. Kyu, 22 September: a prospect's
 * "Connection request sent" read as a Meeting and was counted under Calls &
 * meetings. `linkedin_request` and `reply_received` had both been added to
 * the vocabulary after the chain was written.
 *
 * A lookup with a neutral fallback, and timelineItems.test.ts checks every
 * kind in ACTIVITY_KINDS against it, so the next kind added fails a test
 * instead of turning into a meeting. Kept here rather than imported from
 * leadActivity.ts, which already imports this file.
 */
const TOUCH_TITLES: Record<string, string> = {
  linkedin: 'LinkedIn message',
  linkedin_request: 'Connection request sent',
  linkedin_added: 'Added on LinkedIn',
  call: 'Call',
  text: 'Text',
  meeting: 'Meeting',
  reply_received: 'Reply received',
};

export function touchTitle(kind: string): string | undefined {
  return TOUCH_TITLES[kind];
}

export interface LeadActivityRow {
  id: string;
  kind: string;
  body: string;
  occurred_at: string;
  actor_id: string | null;
  external_id: string | null;
  source: string;
  copy_set_id?: string | null;
  message_slot?: number | null;
  message_selected_at?: string | null;
  message_version?: string | null;
}

/**
 * The contact's lead_activity as Emails, Calls & meetings and Notes.
 * `who` comes from the tracker's own actorLabel so the two timelines agree
 * about whose name goes on a mailbox-synced row.
 */
export function leadActivityItems<R extends LeadActivityRow>(
  rows: readonly R[],
  who: (row: R) => string,
  leadName?: string,
): TimelineItem[] {
  const prefix = leadName === undefined ? '' : `${leadName} · `;
  return rows.map((row) => {
    const kind: TimelineKind =
      row.kind === 'reply_received'
        ? 'email_in'
        : row.kind === 'email_sent'
          ? 'email_out'
          : row.kind === 'note'
            ? 'note'
            : 'touch';
    const title =
      kind === 'email_in'
        ? `${prefix}Email received`
        : kind === 'email_out'
          ? `${prefix}Email sent`
          : kind === 'note'
            ? `${prefix}Note`
            : `${prefix}${TOUCH_TITLES[row.kind] ?? 'Activity'}`;
    const detail = row.body.trim();
    const author = aiAuthorOf(row.source);
    return {
      id: `lead-${row.id}`,
      at: row.occurred_at,
      kind,
      title:
        author === undefined
          ? title
          : `${prefix}${author.name === 'Senku' ? 'Research' : 'Suggested DM'}`,
      ...(detail === ''
        ? {}
        : { detail: detail.length > 240 ? `${detail.slice(0, 237)}…` : detail }),
      who: who(row),
      ...(author === undefined
        ? {}
        : {
            author,
            // Marked, always. A draft nobody wrote should never be mistaken for
            // something a colleague sent.
            badges: [{ label: 'AI', tone: 'neutral' as const }],
          }),
    };
  });
}

export interface TaskRow {
  id: string;
  title: string;
  assignee_id: string | null;
  created_at: string;
  completed_at: string | null;
  /** Absent on the deal-task read, which does not select it; falls back to the assignee. */
  completed_by?: string | null;
  due_date: string | null;
}

/** A task is two moments: when it was placed, and when it was done. */
export function taskItems(
  rows: readonly TaskRow[],
  personName: (id: string | null) => string,
): TimelineItem[] {
  return rows.flatMap((task) => {
    const due = task.due_date === null ? '' : ` · due ${task.due_date}`;
    const placed: TimelineItem = {
      id: `task-${task.id}`,
      at: task.created_at,
      kind: 'task',
      title: `Task: ${task.title}`,
      who: `${personName(task.assignee_id)}${due}`,
    };
    if (task.completed_at === null) return [placed];
    const done: TimelineItem = {
      id: `task-done-${task.id}`,
      at: task.completed_at,
      kind: 'task',
      title: `Done: ${task.title}`,
      who: personName(task.completed_by ?? task.assignee_id),
    };
    return [placed, done];
  });
}

export interface SalesTaskRow {
  id: string;
  title: string;
  assigned_to: string | null;
  created_at: string;
  completed_at: string | null;
  due_on: string | null;
}

/** The tracker's own tasks (sales_tasks), the same two moments. */
export function salesTaskItems(
  rows: readonly SalesTaskRow[],
  personName: (id: string | null) => string,
): TimelineItem[] {
  return taskItems(
    rows.map((row) => ({
      id: row.id,
      title: row.title,
      assignee_id: row.assigned_to,
      created_at: row.created_at,
      completed_at: row.completed_at,
      due_date: row.due_on,
    })),
    personName,
  );
}

export interface OrderRow {
  id: string;
  order_no: string;
  status: string;
  created_at: string;
  status_changed_at: string | null;
  date_delivered: string | null;
}

/** An order is raised, moves status, and is delivered. Three moments at most. */
export function orderItems(
  rows: readonly OrderRow[],
  statusLabel: (id: string) => string,
  href: (id: string) => string,
): TimelineItem[] {
  return rows.flatMap((order) => {
    const items: TimelineItem[] = [
      {
        id: `order-${order.id}`,
        at: order.created_at,
        kind: 'order',
        title: `${order.order_no} raised`,
        href: href(order.id),
      },
    ];
    if (order.status_changed_at !== null && order.status_changed_at !== order.created_at) {
      items.push({
        id: `order-status-${order.id}`,
        at: order.status_changed_at,
        kind: 'order',
        title: `${order.order_no} · ${statusLabel(order.status)}`,
        href: href(order.id),
      });
    }
    if (order.date_delivered !== null) {
      items.push({
        id: `order-delivered-${order.id}`,
        at: order.date_delivered,
        kind: 'order',
        title: `${order.order_no} delivered`,
        href: href(order.id),
      });
    }
    return items;
  });
}
